import * as React from 'react'
import { Calendar, X } from 'lucide-react'
import { Alert, AlertDescription } from './alert'
import { Button } from './button'

export interface PrescriptionDueAlertProps {
  /** Whether the alert should be visible */
  show: boolean
  /** Medication name that is due for refill */
  medicationName: string
  /** Due date for the prescription */
  dueDate: Date
  /** Callback when the schedule visit button is clicked */
  onScheduleVisit: () => void
  /** Whether the alert can be dismissed (optional) */
  dismissible?: boolean
  /** Callback when alert is dismissed (optional) */
  onDismiss?: () => void
  /** Custom className for styling */
  className?: string
}

export function PrescriptionDueAlert({ 
  show, 
  medicationName,
  dueDate,
  onScheduleVisit, 
  dismissible = false, 
  onDismiss,
  className = '' 
}: PrescriptionDueAlertProps) {
  if (!show) return null

  const today = new Date()
  const diffTime = dueDate.getTime() - today.getTime()
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

  const dueDateText = diffDays <= 0 
    ? 'today' 
    : diffDays === 1 
    ? 'tomorrow' 
    : `in ${diffDays} days`

  return (
    <Alert className={`border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/20 ${className}`}>
      <div className="flex items-start justify-between w-full">
        <div className="flex-1">
          <AlertDescription className="text-blue-800 dark:text-blue-200">
            Your <strong>{medicationName}</strong> prescription is due {dueDateText}. Please schedule a visit with your provider to continue your medication. If you'd like to adjust dosages or change your medication you can discuss with your provider during your visit.
          </AlertDescription>
          <Button
            variant="outline"
            size="sm"
            onClick={onScheduleVisit}
            className="mt-3 border-blue-300 text-blue-800 hover:bg-blue-100 dark:border-blue-700 dark:text-blue-200 dark:hover:bg-blue-900/20"
          >
            <Calendar className="h-4 w-4 mr-2" />
            Schedule Visit
          </Button>
        </div>
        {dismissible && onDismiss && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onDismiss}
            className="h-6 w-6 p-0 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    </Alert>
  )
}